import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import { unwrapBettingDoc, unwrapBettingsList } from "../utils/joinedBetsHelpers";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

export const fetchJoinedBets = createAsyncThunk(
  "joinedBets/fetchJoinedBets",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/betting/${userId}`);
      return unwrapBettingsList(res.data);
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load your tickets"));
    }
  }
);

export const deleteJoinedBet = createAsyncThunk(
  "joinedBets/deleteJoinedBet",
  async ({ userId, bettingId }, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/api/betting/${userId}/${bettingId}`);
      return { bettingId, message: res.data?.message };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not delete ticket"));
    }
  }
);

export const editJoinedBetStake = createAsyncThunk(
  "joinedBets/editJoinedBetStake",
  async ({ userId, bettingId, additionalAmount }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/betting/edit/${userId}/${bettingId}`, {
        additionalAmount,
      });
      return unwrapBettingDoc(res.data);
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not update stake"));
    }
  }
);

const joinedBetsSlice = createSlice({
  name: "joinedBets",
  initialState: {
    bets: [],
    status: "idle",
    error: null,
    actionLoading: false,
    actionError: null,
  },
  reducers: {
    clearJoinedBetsError(state) {
      state.error = null;
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJoinedBets.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchJoinedBets.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.bets = action.payload;
      })
      .addCase(fetchJoinedBets.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(deleteJoinedBet.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteJoinedBet.fulfilled, (state, action) => {
        state.actionLoading = false;
        const id = action.payload.bettingId;
        state.bets = state.bets.filter((bet) => String(bet._id) !== String(id));
      })
      .addCase(deleteJoinedBet.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })
      .addCase(editJoinedBetStake.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(editJoinedBetStake.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (!updated?._id) return;
        const idx = state.bets.findIndex(
          (bet) => String(bet._id) === String(updated._id)
        );
        if (idx !== -1) {
          state.bets[idx] = updated;
        }
      })
      .addCase(editJoinedBetStake.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const { clearJoinedBetsError } = joinedBetsSlice.actions;
export default joinedBetsSlice.reducer;
